import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Chat from "@/components/Chat";
import { useAuthState } from "@/hooks/useAuthState";
import { useMessages } from "@/hooks/useMessages";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageCircle, User, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Conversation } from "@/integrations/mysql/types";
import { ConversationWithDetails } from "@/services/messageService";
import { getCoverImageUrl } from "@/lib/imageMap";
import { UserService } from "@/services/userService";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { UserPhotoService } from "@/services/userPhotoService";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MinhaAreaPageShell } from "@/components/MinhaAreaPageShell";


const Messages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [recentConversations, setRecentConversations] = useState<ConversationWithDetails[]>([]);
  const [oldConversations, setOldConversations] = useState<ConversationWithDetails[]>([]);
  const [userPhotos, setUserPhotos] = useState<{[key: number]: string}>({});
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userId, user, loading: authLoading } = useAuthState();
  const { conversations, loading, error } = useMessages();

  // Função para buscar fotos dos usuários
  const fetchUserPhotos = async (conversations: ConversationWithDetails[]) => {
    const userIds = new Set<number>();
    conversations.forEach(conv => {
      userIds.add(conv.guest_id);
      userIds.add(conv.owner_id);
    });

    const photos: {[key: number]: string} = {};
    
    for (const uid of userIds) {
      try {
        const user = await UserService.getUserById(uid);
        if (user?.profile_photo) {
          photos[uid] = user.profile_photo;
        }
      } catch (error) {
        console.error('Erro ao buscar foto do usuário:', uid, error);
      }
    }
    
    setUserPhotos(photos);
  };

  // Detectar parâmetro de conversa na URL e abrir automaticamente
  useEffect(() => {
    const conversationParam = searchParams.get('conversation');
    if (conversationParam && conversations.length > 0) {
      console.log('🔗 Abrindo conversa específica:', conversationParam);
      setSelectedConversation(conversationParam);
      
      // Limpar o parâmetro da URL após abrir
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('conversation');
      const newUrl = newSearchParams.toString() 
        ? `${window.location.pathname}?${newSearchParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams, conversations]);

  // Buscar fotos dos usuários quando as conversas carregarem
  useEffect(() => {
    if (conversations.length > 0) {
      fetchUserPhotos(conversations);
    }
  }, [conversations]);

  // Separar conversas recentes (últimos 90 dias) das antigas e ordenar por não lidas
  useEffect(() => {
    if (conversations.length > 0) {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

      const recent = conversations.filter(conv => {
        const lastMessageDate = new Date(conv.updated_at);
        return lastMessageDate >= ninetyDaysAgo;
      });

      const old = conversations.filter(conv => {
        const lastMessageDate = new Date(conv.updated_at);
        return lastMessageDate < ninetyDaysAgo;
      });

      // Ordenar conversas recentes: primeiro as não lidas, depois por data de atualização
      const sortedRecent = recent.sort((a, b) => {
        // Primeiro: conversas com mensagens não lidas (ordem decrescente por quantidade)
        const aUnreadCount = a.unread_count || 0;
        const bUnreadCount = b.unread_count || 0;
        
        if (aUnreadCount > 0 && bUnreadCount === 0) return -1;
        if (aUnreadCount === 0 && bUnreadCount > 0) return 1;
        if (aUnreadCount > 0 && bUnreadCount > 0) {
          // Se ambos têm não lidas, ordenar por quantidade (mais não lidas primeiro)
          return bUnreadCount - aUnreadCount;
        }
        
        // Segundo: por data de atualização (mais recente primeiro)
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });

      // Ordenar conversas antigas por data de atualização
      const sortedOld = old.sort((a, b) => {
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });

      setRecentConversations(sortedRecent);
      setOldConversations(sortedOld);
    }
  }, [conversations]);

  // Aguardar carregamento da autenticação
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verificando autenticação...</p>
    </div>
    </div>
    );
  }

  // Redirecionar se não estiver autenticado
  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando conversas...</p>
    </div>
    </div>
    );
  }

  const renderConversationCard = (conversation: ConversationWithDetails) => {
    // Determinar o nome da outra pessoa baseado no tipo de usuário
    const otherUserName = userType === 'guest' 
      ? conversation.owner_name || 'Proprietário'
      : conversation.guest_name || 'Hóspede';
    const hasUnread = conversation.unread_count && conversation.unread_count > 0;
    
    // Obter foto do outro usuário
    const otherUserId = userType === 'guest' ? conversation.owner_id : conversation.guest_id;
    const otherUserPhoto = userPhotos[otherUserId];
    
    return (
      <Card
        key={conversation.id}
        className={`cursor-pointer hover:shadow-lg transition-all duration-200 border ${
          hasUnread 
            ? 'border-primary bg-primary/5 shadow-md' 
            : 'border-border'
        }`}
        onClick={() => setSelectedConversation(conversation.id.toString())}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
              <img
                src={getCoverImageUrl(conversation)}
                alt={conversation.property_title}
                className="w-full h-full object-cover"
              />
    </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div>
                    <h3 className={`font-semibold text-base mb-1 line-clamp-1 ${
                      hasUnread ? 'text-primary font-bold' : 'text-foreground'
                    }`}>
                      {conversation.property_title}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Avatar className="h-5 w-5">
                        <AvatarImage 
                          src={otherUserPhoto ? UserPhotoService.getUserPhotoUrl(otherUserId, otherUserPhoto) : undefined} 
                        />
                        <AvatarFallback className="h-5 w-5 text-xs">
                          <User className="h-3 w-3" />
                        </AvatarFallback>
                      </Avatar>
                      <span>Com {otherUserName}</span>
    </div>
    </div>
    </div>
                
                <div className="flex items-center gap-2">
                  {conversation.unread_count && conversation.unread_count > 0 && (
                    <Badge className="bg-primary text-primary-foreground text-xs h-5 w-5 flex items-center justify-center p-0">
                      {conversation.unread_count}
                    </Badge>
                  )}
                  <MessageCircle className="h-4 w-4 text-muted-foreground" />
    </div>
    </div>
              
                {conversation.last_message && (
                  <p className={`text-sm mb-2 line-clamp-2 ${
                    hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'
                  }`}>
                    {conversation.last_message.message}
                  </p>
                )}
              
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>
                  {format(new Date(conversation.updated_at), "dd/MM/yyyy 'às' HH:mm", { 
                    locale: ptBR,
                    timeZone: 'America/Sao_Paulo'
                  })}
                </span>
    </div>
    </div>
    </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <MinhaAreaPageShell>
<div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Mensagens</h1>
          <p className="text-muted-foreground">
            {conversations.length} {conversations.length === 1 ? 'conversa' : 'conversas'} total
          </p>
    </div>

      {conversations.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Nenhuma conversa ainda</h3>
            <p className="text-muted-foreground mb-6">
              Inicie uma conversa com um proprietário através da página do imóvel
            </p>
            <Button onClick={() => navigate("/")}>
              Explorar Propriedades
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="recent" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="recent" className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Conversas Recentes
              {recentConversations.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {recentConversations.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="old" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Conversas Antigas
              {oldConversations.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {oldConversations.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="recent" className="mt-6">
            {recentConversations.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <MessageCircle className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                  <h3 className="text-base font-semibold text-foreground mb-1">Nenhuma conversa recente</h3>
                  <p className="text-muted-foreground text-sm">
                    Suas conversas dos últimos 90 dias aparecerão aqui
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {recentConversations.map(renderConversationCard)}
    </div>
            )}
          </TabsContent>
          
          <TabsContent value="old" className="mt-6">
            {oldConversations.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Clock className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                  <h3 className="text-base font-semibold text-foreground mb-1">Nenhuma conversa antiga</h3>
                  <p className="text-muted-foreground text-sm">
                    Conversas com mais de 90 dias aparecerão aqui
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {oldConversations.map(renderConversationCard)}
    </div>
            )}
          </TabsContent>
        </Tabs>
      )}

        {selectedConversation && (
          <Chat
            conversationId={selectedConversation}
            onClose={() => setSelectedConversation(null)}
          />
        )}
    </MinhaAreaPageShell>
  );
};

export default Messages;
